import React, { useEffect } from 'react';
import { Container, Row, Col, Card, Alert } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import NavigationHeader from './components/NavigationHeader';
import FooterWithPolicies from './components/FooterWithPolicies';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowLeft, 
  faGavel, 
  faHome, 
  faBalanceScale,
  faHandshake,
  faExclamationTriangle,
  faPhone,
  faEnvelope
} from '@fortawesome/free-solid-svg-icons';

const FairHousingStatement: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="policy-page">
      <NavigationHeader />
      
      <Container className="py-5">
        <Row>
          <Col lg={10} className="mx-auto">
            <Link to="/" className="btn btn-link mb-4">
              <FontAwesomeIcon icon={faArrowLeft} /> Back to Home
            </Link>
            
            <Card className="policy-content">
              <Card.Body className="p-5">
                <div className="text-center mb-5">
                  {/* Fair Housing Logo */}
                  <svg 
                    className="mb-4" 
                    viewBox="0 0 200 150" 
                    xmlns="http://www.w3.org/2000/svg"
                    aria-label="Equal Housing Opportunity"
                    style={{ width: '150px', height: 'auto' }}
                  >
                    {/* House */}
                    <path d="M100 30 L50 70 L50 120 L80 120 L80 85 L120 85 L120 120 L150 120 L150 70 Z" 
                          fill="#1a1a1a" 
                          stroke="#1a1a1a" 
                          strokeWidth="2"/>
                    {/* Roof */}
                    <path d="M100 30 L150 70 M100 30 L50 70" 
                          fill="none" 
                          stroke="#1a1a1a" 
                          strokeWidth="4"/>
                    {/* Equal sign */}
                    <rect x="160" y="65" width="25" height="6" fill="#1a1a1a"/>
                    <rect x="160" y="80" width="25" height="6" fill="#1a1a1a"/>
                    {/* Text */}
                    <text x="100" y="140" 
                          textAnchor="middle" 
                          fontSize="16" 
                          fill="#1a1a1a"
                          fontFamily="Arial, sans-serif"
                          fontWeight="bold">
                      EQUAL HOUSING
                    </text>
                  </svg>
                  
                  <h1>Fair Housing Statement</h1>
                  <p className="text-muted">Our Commitment to Equal Housing Opportunity</p>
                </div>

                <Alert variant="info" className="mb-5">
                  <h5 className="alert-heading">
                    <FontAwesomeIcon icon={faBalanceScale} /> Equal Housing Opportunity
                  </h5>
                  <p className="mb-0">
                    KairosIQ AI Realty is committed to the letter and spirit of U.S. policy for the 
                    achievement of equal housing opportunity throughout the Nation. We encourage 
                    and support an affirmative advertising and marketing program in which there 
                    are no barriers to obtaining housing because of race, color, religion, sex, 
                    disability, familial status, or national origin.
                  </p>
                </Alert>

                <div className="policy-section">
                  <h2><FontAwesomeIcon icon={faGavel} /> Our Fair Housing Policy</h2>
                  <p>
                    KairosIQ AI Realty, in partnership with KairosIQ AI technology, is dedicated to 
                    ensuring equal professional service and housing opportunities for all people. 
                    We strictly adhere to all federal, state, and local fair housing laws and 
                    regulations.
                  </p>
                  
                  <p>
                    It is our policy to offer all properties for sale, lease, or rental to all 
                    persons without regard to:
                  </p>
                  
                  <ul className="fair-housing-protected-classes">
                    <li><strong>Race</strong> or color</li>
                    <li><strong>Religion</strong> or creed</li>
                    <li><strong>National origin</strong> or ancestry</li>
                    <li><strong>Sex</strong> or gender</li>
                    <li><strong>Disability</strong> or handicap</li>
                    <li><strong>Familial status</strong> (families with children under 18)</li>
                    <li><strong>Sexual orientation</strong> or gender identity (where protected by local law)</li>
                    <li><strong>Source of income</strong> (where protected by local law)</li>
                    <li><strong>Military status</strong> (where protected by local law)</li>
                  </ul>
                </div>

                <div className="policy-section">
                  <h3><FontAwesomeIcon icon={faHome} /> What This Means for You</h3>
                  
                  <h4>As a Home Buyer or Renter</h4>
                  <p>You have the right to:</p>
                  <ul>
                    <li>View any property listed for sale or rent</li>
                    <li>Receive equal professional service regardless of your protected characteristics</li>
                    <li>Be shown properties in any neighborhood without steering or discrimination</li>
                    <li>Receive accurate and complete information about available properties</li>
                    <li>Have equal opportunity to purchase or rent any available property</li>
                    <li>File a complaint if you experience discrimination</li>
                  </ul>

                  <h4>As a Home Seller or Landlord</h4>
                  <p>You must:</p>
                  <ul>
                    <li>Make your property available to all qualified buyers or renters</li>
                    <li>Use non-discriminatory criteria for evaluating offers or applications</li>
                    <li>Avoid discriminatory advertising or marketing</li>
                    <li>Provide reasonable accommodations for people with disabilities</li>
                    <li>Allow reasonable modifications for accessibility (at tenant's expense for rentals)</li>
                  </ul>
                </div>

                <div className="policy-section">
                  <h3><FontAwesomeIcon icon={faHandshake} /> Our Commitment</h3>
                  
                  <p>KairosIQ AI Realty and all our agents commit to:</p>
                  <ul>
                    <li>Providing equal professional service to all clients and customers</li>
                    <li>Showing and offering all available properties to all persons</li>
                    <li>Maintaining accurate and non-discriminatory property listings</li>
                    <li>Using inclusive marketing and advertising practices</li>
                    <li>Training all staff on fair housing laws and best practices</li>
                    <li>Investigating and addressing any fair housing complaints promptly</li>
                    <li>Ensuring our AI technology is free from discriminatory bias</li>
                  </ul>
                </div>

                <div className="policy-section">
                  <h3>AI Technology and Fair Housing</h3>
                  
                  <p>
                    Our KairosIQ AI technology is specifically designed to promote fair housing:
                  </p>
                  <ul>
                    <li><strong>Bias-Free Algorithms:</strong> Our AI never considers protected characteristics in property recommendations</li>
                    <li><strong>Equal Access:</strong> All users receive the same quality of AI-powered search assistance</li>
                    <li><strong>Inclusive Data:</strong> Training data represents all communities equally</li>
                    <li><strong>Regular Audits:</strong> Independent reviews ensure our AI remains discrimination-free</li>
                    <li><strong>Transparency:</strong> Users can understand why properties are recommended</li>
                  </ul>
                </div>

                <div className="policy-section">
                  <h3>Examples of Prohibited Conduct</h3>
                  
                  <p>The following actions are strictly prohibited:</p>
                  <ul>
                    <li>Refusing to show, sell, or rent based on protected characteristics</li>
                    <li>Steering buyers to or away from certain neighborhoods</li>
                    <li>Providing different terms, conditions, or services based on protected class</li>
                    <li>Making discriminatory statements or advertisements</li>
                    <li>Falsely denying availability of properties</li>
                    <li>Refusing reasonable accommodations for disabilities</li>
                    <li>Intimidating or retaliating against anyone exercising fair housing rights</li>
                  </ul>
                </div>

                <div className="policy-section">
                  <h3>Reasonable Accommodations and Modifications</h3>
                  
                  <p>
                    We are committed to providing reasonable accommodations for persons with 
                    disabilities:
                  </p>
                  <ul>
                    <li>Accessible property showings and open houses</li>
                    <li>Communication in alternative formats when requested</li>
                    <li>Service animals are welcome at all properties</li>
                    <li>Assistance in understanding modification rights and procedures</li>
                    <li>AI interface adaptations for accessibility needs</li>
                  </ul>
                </div>

                <div className="policy-section">
                  <h3><FontAwesomeIcon icon={faExclamationTriangle} /> Reporting Discrimination</h3>
                  
                  <Alert variant="warning">
                    <h5>If You Experience Discrimination</h5>
                    <p>
                      If you believe you have experienced housing discrimination, you have the right 
                      to file a complaint. We take all allegations seriously and will investigate 
                      promptly.
                    </p>
                  </Alert>

                  <h4>How to File a Complaint</h4>
                  
                  <div className="complaint-contacts">
                    <div className="contact-item">
                      <h5>KairosIQ AI Realty Fair Housing Officer</h5>
                      <p>
                        <FontAwesomeIcon icon={faEnvelope} /> <a href="mailto:fairhousing@kairosiq.ai">fairhousing@kairosiq.ai</a><br />
                        <FontAwesomeIcon icon={faPhone} /> 1-602-860-6110 <br />
                        We will respond within 24 hours
                      </p>
                    </div>

                    <div className="contact-item">
                      <h5>U.S. Department of Housing and Urban Development (HUD)</h5>
                      <p>
                        <FontAwesomeIcon icon={faPhone} /> 1-602-860-6110 <br />
                        <FontAwesomeIcon icon={faPhone} /> 1-602-860-6110 <br />
                        <FontAwesomeIcon icon={faEnvelope} /> <a href="https://www.hud.gov/fairhousing" target="_blank" rel="noopener noreferrer">www.hud.gov/fairhousing</a>
                      </p>
                    </div>

                    <div className="contact-item">
                      <h5>Arizona Attorney General's Office</h5>
                      <p>
                        Civil Rights Division<br />
                        <FontAwesomeIcon icon={faPhone} /> (602) 542-5263<br />
                        <FontAwesomeIcon icon={faEnvelope} /> <a href="https://www.azag.gov/civil-rights" target="_blank" rel="noopener noreferrer">www.azag.gov/civil-rights</a>
                      </p>
                    </div>
                  </div>
                </div>

                <div className="policy-section">
                  <h3>Training and Compliance</h3>
                  
                  <p>
                    KairosIQ AI Realty maintains a comprehensive fair housing compliance program:
                  </p>
                  <ul>
                    <li>Mandatory fair housing training for all agents and staff</li>
                    <li>Annual certification requirements</li>
                    <li>Regular updates on fair housing laws and regulations</li>
                    <li>Internal audits of listings and marketing materials</li>
                    <li>Swift investigation and resolution of complaints</li>
                    <li>Continuous improvement of policies and procedures</li>
                  </ul>
                </div>

                <div className="policy-section">
                  <h3>Additional Resources</h3>
                  
                  <p>For more information about fair housing:</p>
                  <ul>
                    <li>
                      <a href="https://www.hud.gov/fairhousing" target="_blank" rel="noopener noreferrer">
                        HUD Fair Housing Information
                      </a>
                    </li>
                    <li>
                      <a href="https://www.justice.gov/crt/fair-housing-act-1" target="_blank" rel="noopener noreferrer">
                        Department of Justice Fair Housing Act
                      </a>
                    </li>
                    <li>
                      <a href="https://www.nar.realtor/fair-housing" target="_blank" rel="noopener noreferrer">
                        National Association of REALTORS® Fair Housing Resources
                      </a>
                    </li>
                    <li>
                      <a href="https://nationalfairhousing.org/" target="_blank" rel="noopener noreferrer">
                        National Fair Housing Alliance
                      </a>
                    </li>
                  </ul>
                </div>

                <div className="fair-housing-footer text-center mt-5">
                  <svg 
                    className="mb-3" 
                    viewBox="0 0 100 100" 
                    xmlns="http://www.w3.org/2000/svg"
                    aria-label="Equal Housing Opportunity"
                    style={{ width: '80px', height: '80px' }}
                  >
                    {/* House */}
                    <path d="M50 15 L20 40 L20 70 L40 70 L40 50 L60 50 L60 70 L80 70 L80 40 Z" 
                          fill="#1a1a1a" 
                          stroke="#1a1a1a" 
                          strokeWidth="2"/>
                    {/* Roof */}
                    <path d="M50 15 L80 40 M50 15 L20 40" 
                          fill="none" 
                          stroke="#1a1a1a" 
                          strokeWidth="3"/>
                    {/* Equal sign */}
                    <rect x="85" y="35" width="12" height="3" fill="#1a1a1a"/>
                    <rect x="85" y="45" width="12" height="3" fill="#1a1a1a"/>
                    {/* Text */}
                    <text x="50" y="85" 
                          textAnchor="middle" 
                          fontSize="8" 
                          fill="#1a1a1a"
                          fontFamily="Arial, sans-serif"
                          fontWeight="bold">
                      EQUAL HOUSING
                    </text>
                    <text x="50" y="94" 
                          textAnchor="middle" 
                          fontSize="8" 
                          fill="#1a1a1a"
                          fontFamily="Arial, sans-serif"
                          fontWeight="bold">
                      OPPORTUNITY
                    </text>
                  </svg>
                  
                  <p className="text-muted">
                    KairosIQ AI Realty supports the Fair Housing Act and equal opportunity housing.
                  </p>
                </div>

                <div className="text-center mt-4">
                  <Link to="/ai-ethics" className="btn btn-outline-primary me-2">
                    AI Ethics Policy
                  </Link>
                  <Link to="/privacy-policy" className="btn btn-outline-primary">
                    Privacy Policy
                  </Link>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      <FooterWithPolicies />

      <style>{`
        .policy-page {
          background-color: #f8f9fa;
          min-height: 100vh;
        }

        .policy-content {
          box-shadow: 0 0 20px rgba(0,0,0,0.1);
          border: none;
        }

        .policy-section {
          margin-bottom: 40px;
        }

        .policy-section h2 {
          color: #333;
          margin-bottom: 20px;
          font-size: 28px;
        }

        .policy-section h3 {
          color: #333;
          margin-bottom: 15px;
          margin-top: 30px;
          font-size: 22px;
        }

        .policy-section h4 {
          color: #555;
          margin-bottom: 10px;
          margin-top: 20px;
          font-size: 18px;
        }

        .policy-section p {
          line-height: 1.7;
          color: #555;
        }

        .policy-section ul {
          line-height: 1.8;
          color: #555;
        }

        .policy-section ul li {
          margin-bottom: 8px;
        }

        .fair-housing-protected-classes {
          background-color: #f8f9fa;
          padding: 20px;
          border-radius: 8px;
          margin: 20px 0;
        }

        .complaint-contacts {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 20px;
          margin-top: 20px;
        }

        .contact-item {
          background-color: #f8f9fa;
          padding: 20px;
          border-radius: 8px;
          border-left: 4px solid #ffc107;
        }

        .contact-item h5 {
          color: #333;
          margin-bottom: 10px;
        }

        .contact-item p {
          margin: 0;
          line-height: 1.8;
        }

        .fair-housing-footer {
          padding-top: 40px;
          border-top: 1px solid #e9ecef;
        }
      `}</style>
    </div>
  );
};

export default FairHousingStatement;